/**
 * CrewSlotEditor — the workshop Crew editor (BON-200). Three role SECTIONS
 * (Teacher → Assistant → Trainee), each a list of assigned people (avatar +
 * name + ✕ remove) plus a dashed "+ Add {role}" button that opens a role-scoped
 * single-person picker modal.
 *
 * Edits mutate a LOCAL DRAFT only; nothing persists until "Save crew" commits
 * the full desired set in ONE PUT /:id/staff (via {@link useReplaceWorkshopStaff}).
 * An "Unsaved crew changes" bar with Cancel/Save crew appears while the draft
 * differs from the saved crew. Read-only once the workshop has started/past, or
 * for the Teacher persona — then no buttons, no bar, just a "Read-only" pill.
 *
 * Reusable: it takes the saved crew + region + a commit fn, so the same editor
 * can mount in the detail right rail or any other crew-editing pane.
 */
import { useState } from 'react';

import type { CrewPosition, CrewSlotInput, WorkshopCrewMember } from '../../../../api/workshops';
import { StatusPill } from '../../primitives';

import { CREW_ROLES, canAddRole, membersForRole } from './crewModel';
import { CrewPickerModal } from './CrewPickerModal';
import { RoleSection } from './RoleSection';
import { UnsavedBar } from './UnsavedBar';
import { useCrewDraft } from './useCrewDraft';

export interface CrewSlotEditorProps {
  /** The workshop's saved crew (source of truth for the draft + diff). */
  staff: WorkshopCrewMember[];
  /** Region the venue belongs to — scopes the picker. */
  regionId: string | null;
  /** Editable when false; read-only (no buttons/bar) when true. */
  readOnly: boolean;
  /** Copy for the read-only pill, e.g. "Workshop started". */
  readOnlyReason?: string;
  /** True once the workshop is published (BON-466). A teacher is needed for the
   *  workshop to RUN, but is no longer a precondition of publishing — so the
   *  unfilled-teacher warning only turns urgent once the workshop is live. */
  published?: boolean;
  /** Commit the full desired crew set — resolves on success, rejects on failure. */
  onSave: (staff: CrewSlotInput[]) => Promise<unknown>;
  saving?: boolean;
}

export function CrewSlotEditor({
  staff,
  regionId,
  readOnly,
  readOnlyReason = 'Read-only',
  published = false,
  onSave,
  saving = false,
}: CrewSlotEditorProps) {
  const { draft, dirty, error, excludedUserIds, add, remove, cancel, commit } = useCrewDraft(
    staff,
    onSave,
  );
  const [picking, setPicking] = useState<CrewPosition | null>(null);

  const onPick = (member: { userId: string; name: string }) => {
    if (picking) add(member, picking);
    setPicking(null);
  };

  return (
    <section className="nb-card">
      <div
        className="nb-card-hd"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <h3>Crew</h3>
        {readOnly && <StatusPill tone="mocked" label={readOnlyReason} />}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {CREW_ROLES.map((position) => (
          <RoleSection
            key={position}
            position={position}
            members={membersForRole(draft, position)}
            readOnly={readOnly}
            published={published}
            canAdd={canAddRole(draft, position)}
            onRemove={remove}
            onAdd={() => setPicking(position)}
          />
        ))}
      </div>

      {!readOnly && dirty && (
        <UnsavedBar saving={saving} onCancel={cancel} onSave={() => void commit()} />
      )}
      {error && (
        <div role="alert" style={{ fontSize: 11.5, color: '#c0392b', marginTop: 8 }}>
          {error}
        </div>
      )}

      {picking && (
        <CrewPickerModal
          position={picking}
          regionId={regionId}
          excludedUserIds={excludedUserIds}
          onPick={onPick}
          onClose={() => setPicking(null)}
        />
      )}
    </section>
  );
}
